import DOMPurify from 'dompurify';
import { attachmentUrl } from '../api/client';
import type { Attachment } from '../api/types';

/**
 * Makes an email body safe to put in the DOM.
 *
 * IMPORTANT, and contrary to what the rebuild plan originally assumed:
 * **SnappyMail does NOT sanitize on the server.** Its `html` field is the raw
 * body straight out of the MIME part; the cleaning lives in its own client
 * (dev/Common/Html.js, `cleanHtml`). Rendering that field directly would be a
 * straightforward XSS hole -- any HTML email could run script in the webmail
 * origin, which is the origin holding the session cookie.
 *
 * So sanitization is ours to do, and it is done with DOMPurify rather than a
 * hand-written filter. Email HTML is adversarial input and a bespoke stripper is
 * a losing game.
 */

/**
 * Remote images are blocked until the reader asks for them.
 *
 * A tracking pixel is a remote image, and loading one on open tells the sender
 * the mail was read, from which IP, and when. Blocking by default is both a
 * privacy win and the behaviour every serious mail client has.
 */
const REMOTE = /^(https?:)?\/\//i;

export interface SanitizeResult {
	html: string;
	/** True when at least one remote image was withheld. */
	blockedRemoteImages: boolean;
}

export function sanitizeEmailHtml(
	rawHtml: string,
	attachments: Attachment[],
	options: { allowRemoteImages: boolean }
): SanitizeResult {
	let blocked = false;

	// Inline images arrive as cid: references pointing at MIME parts. Map them to
	// the Raw view endpoint so a signature logo or embedded screenshot renders,
	// while everything else about the message stays locked down.
	const byContentId = new Map<string, Attachment>();
	for (const attachment of attachments) {
		const id = (attachment.cId || '').replace(/^<|>$/g, '');
		if (id) {
			byContentId.set(id, attachment);
		}
	}

	const hook = (node: Element) => {
		if (!(node instanceof Element)) {
			return;
		}

		if ('IMG' === node.tagName) {
			const src = node.getAttribute('src') ?? '';

			if (src.toLowerCase().startsWith('cid:')) {
				const found = byContentId.get(src.slice(4).replace(/^<|>$/g, ''));
				if (found) {
					node.setAttribute('src', attachmentUrl(found, 'View'));
				} else {
					node.removeAttribute('src');
				}
				return;
			}

			if (REMOTE.test(src) && !options.allowRemoteImages) {
				blocked = true;
				node.removeAttribute('src');
				node.removeAttribute('srcset');
				node.setAttribute('data-blocked', 'true');
				return;
			}

			// data: URIs are left to DOMPurify's own allowlist; anything it did not
			// recognise is not something we should reinstate here.
		}

		if ('A' === node.tagName) {
			// Links open away from the app. `noopener` matters because without it
			// the opened page gets a handle on this window via window.opener.
			node.setAttribute('target', '_blank');
			node.setAttribute('rel', 'noopener noreferrer nofollow');
		}
	};

	DOMPurify.addHook('afterSanitizeAttributes', hook);

	try {
		const html = DOMPurify.sanitize(rawHtml, {
			// Explicitly drop anything that can execute, navigate the top frame, or
			// pull in a subdocument. DOMPurify blocks these by default; naming them
			// means a future config change cannot quietly re-enable them.
			FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'base', 'link', 'meta'],
			FORBID_ATTR: ['srcdoc', 'formaction', 'ping'],
			// Inline `style` attributes are kept: without them a huge amount of real
			// mail collapses into unreadable stacked text. DOMPurify parses and
			// filters their contents, which is what makes that safe -- unlike
			// <style> blocks, which can reach outside the message.
			ALLOW_DATA_ATTR: false,
			WHOLE_DOCUMENT: false,
			RETURN_DOM: false,
			RETURN_DOM_FRAGMENT: false
		});

		return { html, blockedRemoteImages: blocked };
	} finally {
		// Hooks are global to the module, so leaving this attached would apply the
		// previous message's attachment map to the next one.
		DOMPurify.removeHook('afterSanitizeAttributes');
	}
}

/** Escapes a plain-text body for display, preserving its line breaks via CSS. */
export function escapePlain(text: string): string {
	return text
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;');
}

/**
 * Sanitizes markup we are about to SEND.
 *
 * Applied to the composer's own output as well as to quoted originals. The
 * editor produces most of this, but pasted content, link targets and the body
 * of the message being replied to all pass through it -- and nothing should
 * leave here that we would refuse to render coming in.
 *
 * No cid: rewriting and no image blocking: those are reading concerns, and a
 * recipient's client will apply its own.
 */
export function sanitizeOutgoingHtml(html: string): string {
	return DOMPurify.sanitize(html, {
		FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'base', 'link', 'meta'],
		FORBID_ATTR: ['srcdoc', 'formaction', 'ping'],
		ALLOW_DATA_ATTR: false,
		WHOLE_DOCUMENT: false,
		RETURN_DOM: false,
		RETURN_DOM_FRAGMENT: false
	});
}
